/**
 * Format a Y axis numeric value into a compact string with units (M/K) for chart labels.
 * @param {number} val
 * @returns {string}
 */
export const formatYAxis = (val: number) => {
  const abs = Math.abs(val);
  if (abs >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(val / 1_000).toFixed(0)}K`;
  return val.toString();
};

/**
 * Produce a human-friendly percentage/label for variance between previous and current values.
 * Handles special cases like new items and full drop-offs.
 */
export const getPercentageLabel = (
  previousCost: number,
  currentCost: number,
  variancePercentage: number,
): string => {
  if (previousCost === 0 && currentCost === 0) return "—";
  if (previousCost === 0 && currentCost > 0) return "New";
  if (previousCost > 0 && currentCost === 0) return "\u2212100%";
  const val = Math.round(variancePercentage);
  if (val === 0) return "0%";
  if (Math.abs(val) > 100) return val > 0 ? ">100%" : "<\u2212100%";
  return val > 0 ? `+${val}%` : `\u2212${Math.abs(val)}%`;
};

/**
 * Determine label style (pos/neg/neutral) based on previous and current cost and variance.
 */
export const getLabelStyle = (
  previousCost: number,
  currentCost: number,
  variancePercentage: number,
): "pos" | "neg" | "neutral" => {
  if (previousCost === 0 && currentCost === 0) return "neutral";
  if (previousCost === 0 && currentCost > 0) return "pos";
  if (previousCost > 0 && currentCost === 0) return "neg";
  if (variancePercentage === 0) return "neutral";
  return variancePercentage > 0 ? "pos" : "neg";
};

/**
 * Generate a map of ISO date -> label for X axis rendering based on date range heuristics.
 * - Short ranges show full day labels
 * - Monthly and longer ranges show sparse labels to avoid overlap
 */
export const getXLabels = (rawDates: string[]) => {
  if (!rawDates || rawDates.length === 0) return {};

  const isoDates = rawDates.map((d) => new Date(d).toISOString());

  const first = new Date(isoDates[0]);
  const last = new Date(isoDates[isoDates.length - 1]);

  const totalDays =
    Math.floor((last.getTime() - first.getTime()) / (1000 * 3600 * 24)) + 1;

  const sameMonth = first.getMonth() === last.getMonth();
  const isFullMonth =
    sameMonth && first.getDate() === 1 && last.getDate() >= 28;

  const map: Record<string, string> = {};

  isoDates.forEach((iso) => {
    const d = new Date(iso);
    const day = d.getDate();
    const month = d.toLocaleString("default", { month: "short" });

    if (totalDays === 1) {
      map[iso] = `${month} ${day}`;
      return;
    }

    if (totalDays <= 7) {
      map[iso] = `${month} ${day}`;
      return;
    }

    if (isFullMonth) {
      const allowed = [1, 7, 14, 21, 28];
      map[iso] = allowed.includes(day) ? `${month} ${day}` : " ";
      return;
    }

    if (totalDays <= 30) {
      const allowed = [1, 5, 10, 15, 20, 25, 30];
      map[iso] = allowed.includes(day) ? `${month} ${day}` : " ";
      return;
    }

    map[iso] = day === 1 ? `${month}` : " ";
  });

  return map;
};
